import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface CardProps {
  category: string;
  categoryColor?: string;
  headline: string;
  description: string;
  ctaLabel: string;
  ctaHref?: string;
  ctaAction?: () => void;
  imageUrl: string;
  imageAlt: string;
  badge?: string;
  badgeBg?: string;
  detailHref?: string;
  delay?: number;
}

function ServiceCard({
  category, categoryColor = '#4CAF50', headline, description,
  ctaLabel, ctaHref, ctaAction, imageUrl, imageAlt, badge, badgeBg,
  delay = 0,
}: CardProps) {
  const { ref, visible } = useScrollAnimation();
  const navigate = useNavigate();

  const handleCta = () => {
    if (ctaHref) { navigate(ctaHref); }
    else if (ctaAction) { ctaAction(); }
  };

  const isBook = ctaLabel.toLowerCase().startsWith('book');

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col sm:flex-row relative hover:border-[#4CAF50]/50 hover:-translate-y-1 transition-all duration-300 group ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      {/* Image */}
      <div className="w-full h-44 sm:h-auto sm:w-[175px] flex-shrink-0 overflow-hidden order-1 sm:order-2">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Text area */}
      <div className="flex-1 p-4 sm:p-6 flex flex-col justify-between z-10 min-w-0 order-2 sm:order-1">
        <div>
          {badge && (
            <span
              className="inline-block text-white text-[10px] font-bold px-2.5 py-1 rounded-md mb-2 uppercase tracking-wide"
              style={{ background: badgeBg || '#7AC943' }}
            >
              {badge}
            </span>
          )}
          <div className="text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: categoryColor }}>
            {category}
          </div>
          <h3 className="font-heading font-bold text-[#1F2933] text-base sm:text-lg leading-tight mb-1.5">
            {headline}
          </h3>
          <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">{description}</p>
        </div>
        <div className="mt-4">
          <button
            onClick={handleCta}
            className={`flex items-center gap-2 font-bold px-5 py-2.5 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${isBook ? 'bg-[#f59e0b] hover:bg-[#d97706] text-white' : 'bg-gradient-to-r from-[#145A32] to-[#4CAF50] text-white hover:opacity-90'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${isBook ? 'ri-calendar-check-line' : 'ri-arrow-right-line'} text-sm`} />
            </div>
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ServiceSelector() {
  const { ref, visible } = useScrollAnimation();
  const navigate = useNavigate();
  const scrollToQuote = () => document.getElementById('quote-section')?.scrollIntoView({ behavior: 'smooth' });

  const services: CardProps[] = [
    {
      category: 'SOLAR PANELS',
      categoryColor: '#145A32',
      headline: 'Cut energy bills by up to 70% with solar',
      description: 'MCS-certified solar panel installation for homes and businesses. Fixed prices, finance options and next-day surveys available nationwide.',
      ctaLabel: 'View Solar',
      ctaHref: '/solar',
      imageUrl: 'https://public.readdy.ai/ai/img_res/f46fd022197021eb31d0df47420c2cfe.jpg',
      imageAlt: 'Solar panels installation',
      badge: 'MOST POPULAR',
      badgeBg: '#145A32',
      delay: 0,
    },
    {
      category: 'BATTERY STORAGE',
      categoryColor: '#145A32',
      headline: 'Store solar energy & slash your bills further',
      description: 'Home battery storage that keeps your solar power working overnight. Tesla Powerwall & GivEnergy available. Save up to £700 a year.',
      ctaLabel: 'View Battery',
      ctaHref: '/battery',
      imageUrl: 'https://readdy.ai/api/search-image?query=modern%20home%20battery%20storage%20unit%20mounted%20on%20white%20wall%20interior%2C%20sleek%20Tesla%20Powerwall%20style%20battery%20energy%20storage%20system%2C%20glossy%20white%20finish%2C%20clean%20garage%20background%2C%20professional%20product%20photography&width=350&height=260&seq=card-battery-v2&orientation=landscape',
      imageAlt: 'Home battery storage',
      delay: 100,
    },
    {
      category: 'REPAIRS & SERVICING',
      categoryColor: '#145A32',
      headline: 'Expert solar & battery repairs',
      description: 'Fault finding, inverter replacement and full system repairs for all major brands. Annual servicing plans available. Fixed-price, no surprises.',
      ctaLabel: 'Book a Repair',
      ctaAction: scrollToQuote,
      imageUrl: 'https://readdy.ai/api/search-image?query=professional%20solar%20engineer%20technician%20in%20hi-vis%20vest%20on%20residential%20rooftop%20inspecting%20solar%20panels%2C%20holding%20tablet%20for%20diagnostics%2C%20bright%20sunny%20day%20UK%20home%2C%20safety%20helmet%2C%20realistic%20photography&width=350&height=260&seq=card-repairs-v2&orientation=landscape',
      imageAlt: 'Solar repair service',
      delay: 200,
    },
    {
      category: 'MAINTENANCE PLANS',
      categoryColor: '#4CAF50',
      headline: 'Protect your solar investment year-round',
      description: 'Annual servicing plans keep your system running at peak efficiency. Unlimited call-outs, free repairs and priority engineer bookings included.',
      ctaLabel: 'Book a Service',
      ctaAction: scrollToQuote,
      imageUrl: 'https://readdy.ai/api/search-image?query=solar%20panel%20maintenance%20engineer%20with%20clipboard%20checklist%20on%20residential%20roof%2C%20performance%20monitoring%20tablet%2C%20professional%20service%20photography%2C%20bright%20modern%20British%20home&width=350&height=260&seq=card-maintenance-v2&orientation=landscape',
      imageAlt: 'Solar maintenance plan',
      badge: 'UNLIMITED COVER',
      badgeBg: '#145A32',
      delay: 300,
    },
    {
      category: 'SOLAR & BATTERY BUNDLE',
      categoryColor: '#4CAF50',
      headline: 'Buy solar, get battery at half price',
      description: 'Get solar panels and battery storage together and save even more. Our best-value package — MCS certified engineers, fixed price guaranteed.',
      ctaLabel: 'View Bundle',
      ctaHref: '/solar',
      imageUrl: 'https://readdy.ai/api/search-image?query=UK%20residential%20home%20exterior%20with%20solar%20panels%20on%20roof%20and%20home%20battery%20storage%20visible%20inside%20garage%2C%20daytime%20photography%2C%20modern%20property%2C%20green%20energy%2C%20sunny%20weather%2C%20professional%20real%20estate%20photography&width=350&height=260&seq=card-bundle-v2&orientation=landscape',
      imageAlt: 'Solar and battery bundle',
      badge: 'BEST VALUE',
      badgeBg: '#4CAF50',
      delay: 400,
    },
    {
      category: 'EV CHARGING',
      categoryColor: '#145A32',
      headline: 'Commercial & home EV charger installation',
      description: 'OZEV-approved EV chargers for homes, businesses, car parks and fleets. Covering London, Essex, Suffolk & Cambridge.',
      ctaLabel: 'View EV Charging',
      ctaHref: '/ev-charger',
      imageUrl: 'https://readdy.ai/api/search-image?query=modern%20electric%20vehicle%20EV%20charger%20unit%20mounted%20on%20home%20garage%20wall%2C%20white%20charging%20point%20device%2C%20EV%20charging%20cable%20connected%2C%20bright%20clean%20interior%20background%2C%20product%20photography&width=350&height=260&seq=card-ev-v2&orientation=landscape',
      imageAlt: 'EV charger installation',
      badge: 'OZEV APPROVED',
      badgeBg: '#3F4652',
      delay: 500,
    },
  ];

  return (
    <section id="solar" className="bg-[#f7f8fa] py-10 md:py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div
          ref={ref}
          className={`text-center mb-8 md:mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">OUR SERVICES</div>
          <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">
            Solar, battery, maintenance &amp; EV charging
          </h2>
          <p className="text-gray-500 text-sm md:text-base mt-2 max-w-xl mx-auto">
            Everything you need for a greener home or business — solar panels, battery storage, annual servicing, repairs &amp; EV charger installation. Fixed prices, MCS &amp; OZEV certified engineers.
          </p>
        </div>

        {/* Mobile: horizontal swipe slider */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-3 px-0.5">
            <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider">
              {services.length} services
            </span>
            <span className="text-[11px] text-[#4CAF50] font-semibold flex items-center gap-1">
              <i className="ri-drag-move-line text-xs" /> Swipe to explore
            </span>
          </div>
          <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory pb-4 [&::-webkit-scrollbar]:hidden -mx-4 px-4">
            {services.map((svc, i) => (
              <div key={i} className="snap-start flex-shrink-0 w-[82vw]">
                <ServiceCard {...svc} delay={0} />
              </div>
            ))}
          </div>
          {/* Dot indicators */}
          <div className="flex justify-center gap-1.5 mt-1">
            {services.map((_, i) => (
              <div
                key={i}
                className={`rounded-full transition-all ${i === 0 ? 'w-4 h-1.5 bg-[#4CAF50]' : 'w-1.5 h-1.5 bg-gray-300'}`}
              />
            ))}
          </div>
        </div>

        {/* Desktop: 2-col grid */}
        <div className="hidden md:grid grid-cols-2 gap-4">
          {services.map((svc, i) => (
            <ServiceCard key={i} {...svc} />
          ))}
        </div>
      </div>
    </section>
  );
}
